// Static data for WrenchOnWheels
import {
  Stethoscope,
  Droplet,
  Disc3,
  BatteryCharging,
  Wrench,
  Gauge,
  Thermometer,
  Wind,
  CircleDot,
  Lightbulb,
} from "lucide-react";

export const services = [
  {
    id: "diagnostics",
    name: "OBD-II Diagnostics",
    short: "Full system scan with live data analysis.",
    description:
      "Plug-in diagnostic scan for engine, transmission, ABS and airbag systems. Detailed report within 30 minutes.",
    priceMin: 890,
    priceMax: 1600,
    duration: 30,
    Icon: Stethoscope,
    keywords: ["check engine", "warning", "light", "scan", "code"],
  },
  {
    id: "oil",
    name: "Oil & Filter Change",
    short: "Synthetic or conventional, your choice.",
    description:
      "Up to 5 litres of premium oil, new filter, fluid top-up and courtesy 20-point inspection.",
    priceMin: 1250,
    priceMax: 2300,
    duration: 40,
    Icon: Droplet,
    keywords: ["oil", "lube", "leak", "dark oil"],
  },
  {
    id: "brakes",
    name: "Brake Pad Replacement",
    short: "Front or rear pads, on-site.",
    description:
      "Ceramic or semi-metallic pads installed. Rotor inspection and brake fluid check included.",
    priceMin: 3250,
    priceMax: 6100,
    duration: 75,
    Icon: Disc3,
    keywords: ["brake", "squeak", "grinding", "soft pedal", "squeal"],
  },
  {
    id: "battery",
    name: "Battery Replacement",
    short: "New battery installed in 20 min.",
    description:
      "Premium AGM or standard battery options. Old battery recycled, terminals cleaned, system tested.",
    priceMin: 2900,
    priceMax: 5000,
    duration: 25,
    Icon: BatteryCharging,
    keywords: ["won't start", "wont start", "dead", "battery", "crank", "clicking"],
  },
  {
    id: "alternator",
    name: "Alternator Service",
    short: "Charging system repair.",
    description:
      "Alternator testing and replacement with OEM-quality parts. Belt tension check included.",
    priceMin: 5800,
    priceMax: 10500,
    duration: 120,
    Icon: Gauge,
    keywords: ["dim lights", "no charge", "alternator", "battery drain"],
  },
  {
    id: "ac",
    name: "A/C Recharge",
    short: "R-1234yf / R-134a recharge.",
    description:
      "Full A/C performance test, leak check and refrigerant recharge to factory spec.",
    priceMin: 1800,
    priceMax: 3400,
    duration: 45,
    Icon: Wind,
    keywords: ["air con", "a/c", "hot air", "cooling", "ac"],
  },
  {
    id: "coolant",
    name: "Coolant Flush",
    short: "Stop overheating before it starts.",
    description:
      "Full drain, flush and refill with manufacturer-specified coolant. Thermostat inspection.",
    priceMin: 1950,
    priceMax: 2900,
    duration: 50,
    Icon: Thermometer,
    keywords: ["overheat", "temperature", "steam", "coolant", "hot"],
  },
  {
    id: "tires",
    name: "Tire Rotation & Balance",
    short: "Even wear, smoother ride.",
    description:
      "4-wheel rotation, computer balance and pressure reset. TPMS reset included.",
    priceMin: 1050,
    priceMax: 1600,
    duration: 35,
    Icon: CircleDot,
    keywords: ["vibration", "shake", "uneven wear", "tire", "wobble"],
  },
  {
    id: "tune",
    name: "Spark Plug Tune-up",
    short: "Fresh plugs, smoother idle.",
    description:
      "Iridium plugs installed, coil inspection, throttle body cleaning for better fuel economy.",
    priceMin: 2700,
    priceMax: 5200,
    duration: 60,
    Icon: Wrench,
    keywords: ["misfire", "rough idle", "hesitation", "jerk", "stutter"],
  },
  {
    id: "headlight",
    name: "Headlight Restoration",
    short: "See clearly again.",
    description:
      "Wet-sand, polish and UV seal. Restores cloudy headlights to crystal clear.",
    priceMin: 1450,
    priceMax: 2150,
    duration: 45,
    Icon: Lightbulb,
    keywords: ["foggy", "cloudy", "dim", "headlight"],
  },
];

export const carBrands = [
  { name: "Toyota", models: ["Hilux", "Corolla", "Fortuner", "RAV4", "Starlet", "Land Cruiser"] },
  { name: "Volkswagen", models: ["Polo", "Golf", "Tiguan", "T-Cross", "Amarok", "Golf GTI"] },
  { name: "Ford", models: ["Ranger", "EcoSport", "Everest", "Figo", "Mustang", "Puma"] },
  { name: "Hyundai", models: ["i20", "Tucson", "Creta", "Venue", "Grand Creta", "Santa Fe"] },
  { name: "Kia", models: ["Picanto", "Seltos", "Sportage", "Sonet", "Sorento", "Carnival"] },
  { name: "Nissan", models: ["NP200", "NP300", "Navara", "Qashqai", "X-Trail", "Magnite"] },
  { name: "BMW", models: ["3 Series", "5 Series", "X3", "X5", "1 Series", "M3"] },
  { name: "Mercedes-Benz", models: ["C-Class", "E-Class", "GLC", "GLE", "A-Class", "S-Class"] },
  { name: "Audi", models: ["A4", "A3", "Q5", "Q3", "A6", "RS5"] },
  { name: "Isuzu", models: ["D-Max", "MU-X"] },
  { name: "Suzuki", models: ["Swift", "Vitara", "Jimny", "Baleno", "S-Presso", "Fronx"] },
  { name: "Renault", models: ["Kwid", "Duster", "Triber", "Kiger", "Clio"] },
  { name: "Honda", models: ["Fit", "HR-V", "CR-V", "Civic", "Amaze", "WR-V"] },
  { name: "Mazda", models: ["CX-5", "CX-30", "Mazda2", "Mazda3", "CX-3"] },
  { name: "Tesla", models: ["Model 3", "Model Y", "Model S", "Model X"] },
  { name: "Jeep", models: ["Wrangler", "Grand Cherokee", "Compass", "Renegade", "Gladiator"] },
  { name: "Subaru", models: ["XV", "Outback", "Forester", "WRX", "Impreza"] },
];

export const testimonials = [
  {
    name: "Maya Rodriguez",
    role: "Designer · Cape Town",
    rating: 5,
    text: "They fixed my alternator in my driveway before my 8am meeting. The tracker even shows the mechanic's ETA like a rideshare — game changer.",
  },
  {
    name: "Darius Khan",
    role: "Small business owner · Johannesburg",
    rating: 5,
    text: "Three vans, three mechanics, one morning. Saved me two days of downtime. The transparent pricing is exactly what fleets need.",
  },
  {
    name: "Chloe Wenger",
    role: "New parent · Durban",
    rating: 5,
    text: "Couldn't leave the baby. Booked a brake job on my phone during a nap. Done by lunch. Felt like magic.",
  },
  {
    name: "Tomás Alvarez",
    role: "Engineer · Pretoria",
    rating: 5,
    text: "Smart diagnosis flagged my coolant issue before I even called. The mechanic arrived with the right part. Zero guesswork.",
  },
  {
    name: "Priya Shah",
    role: "Founder · Sandton",
    rating: 5,
    text: "The booking summary updates in real time — I saw exactly what I was paying for. No surprises. Finally, a mechanic I trust.",
  },
  {
    name: "Jordan Blake",
    role: "Teacher · Stellenbosch",
    rating: 5,
    text: "Battery died in the Checkers parking lot in Stellenbosch. 22 minutes later, new battery, back on the road. Unreal service.",
  },
];

export const faqs = [
  {
    q: "How fast can a mechanic arrive?",
    a: "For standard bookings, typical arrival is within 60–90 minutes in our service zones. Urgent/emergency requests are prioritized with an average ETA of 25–40 minutes during business hours (7am–9pm).",
  },
  {
    q: "Where do you operate?",
    a: "We currently cover major metros across South Africa within a 40 km service radius. Use the location input during booking to instantly confirm coverage at your address.",
  },
  {
    q: "Are your mechanics certified?",
    a: "Every WrenchOnWheels technician is certified, fully insured, and background-checked. We also carry R18M liability coverage on every job.",
  },
  {
    q: "What if the problem can't be fixed on-site?",
    a: "If we can't safely complete the repair at your location — for example, major transmission work — your diagnostic fee is credited toward a partner shop tow and repair.",
  },
  {
    q: "How does pricing work?",
    a: "Every service shows a transparent price range upfront. The final quote is confirmed before any work starts. No hidden fees, ever.",
  },
  {
    q: "Do you service electric vehicles?",
    a: "Yes. Our EV-certified technicians service Tesla, BMW iX, Volvo XC40 Recharge and most mainstream EVs for brakes, tyres, 12V batteries, filters and coolant loops.",
  },
];
